import libphonenumber, { PhoneNumberFormat } from "google-libphonenumber";
import jwtDecode from "jwt-decode";
import { httpsCallable } from "firebase/functions";
import { functionsInstance } from "firebase-setup";
import _, { isEmpty } from "lodash";
import { Typography } from "@mui/material";
import { marked } from "marked";
import axios from "axios";
import { FHIRSave, HTTPAidaBaseService } from "adapters/http";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// We use an ephemeral, in-memory chat history for this demo.
import { InMemoryChatMessageHistory } from "@langchain/core/chat_history";
import { ChatPromptTemplate } from "@langchain/core/prompts";
import { RunnableWithMessageHistory } from "@langchain/core/runnables";
import i18n from "multilingual";
import { patientObject } from "redux/actions/PatientActions";
import { encounterObject } from "redux/actions/EncounterActions";

const messageHistories = {};

const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

// Function to format a phone number based on country
export const formatPhoneNumber = (phoneNumber, countryCode = "US") => {
  try {
    const number = phoneUtil.parseAndKeepRawInput(phoneNumber, countryCode);
    return phoneUtil.format(
      number,
      libphonenumber.PhoneNumberFormat.INTERNATIONAL
    );
  } catch (error) {
    console.error("Invalid number:", error);
    return null;
  }
};

export const formatToE164 = (phoneNumber) => {
  try {
    const parsedNumber = phoneUtil.parse(phoneNumber); // works if input has '+' prefix
    if (phoneUtil.isValidNumber(parsedNumber)) {
      return phoneUtil.format(parsedNumber, PhoneNumberFormat.E164);
    }
    return null;
  } catch {
    return null;
  }
};

export const isCountryCode = (input) => {
  const code = input.replace('+', '')
  const region = phoneUtil.getRegionCodeForCountryCode(parseInt(code))
  return region !== null && region !== 'ZZ'
}

export const extractFirstTwoParamsFromUrl = (url) => {
  const matches = url.match(/^\/([^/]+)\/([^/]+)/);
  if (matches && matches.length === 3) {
    return `/${matches[1]}/${matches[2]}`;
  } else {
    return url;
  }
};

export const getTenantFromToken = () => {
  let token = localStorage?.getItem("jwtToken");
  if (token) {
    token = jwtDecode(token);
    return token?.firebase?.tenant || "";
  } else return "";
};

function mergePartsInto(lastObject, obj) {
  // Adjust this logic depending on what "merge" means for your data.
  // Here, it's simply combining other fields assuming they're not arrays or objects.
  for (const key in obj) {
    if (key === "parts") {
      if (lastObject.hasOwnProperty(key)) {
        let text = lastObject[key]?.[0]?.text;
        text += obj[key]?.[0]?.text; // Assuming you can sum the properties
        lastObject[key] = [{ text }];
      } else {
        lastObject[key] = obj[key];
      }
    }
  }
}

function mergeObjectsByRole(objects) {
  const merged = [];

  objects.forEach((obj) => {
    if (merged.length === 0) {
      // If the merged array is empty, add the first object
      merged.push({ ...obj });
    } else {
      const lastObject = merged[merged.length - 1];

      if (lastObject.role === obj.role) {
        // If the current object has the same role as the last object in the result array,
        // merge them.
        mergePartsInto(lastObject, obj);
      } else {
        // If roles are different, simply add the current object to the results
        merged.push({ ...obj });
      }
    }
  });

  return merged;
}

export const generateResponseLanchainHelper = async (
  message,
  { backstory = "", sessionId = "jenny_chat_history", chatModel = "" } = {}
) => {
  if (!chatModel) {
    console.error("No chat model provided");
  }

  const prompt = ChatPromptTemplate.fromMessages([
    ["system", `${backstory}`],
    ["placeholder", "{chat_history}"],
    ["human", "{input}"],
  ]);

  const chain = prompt.pipe(chatModel);

  const withMessageHistory = new RunnableWithMessageHistory({
    runnable: chain,

    getMessageHistory: async (sessionId) => {
      if (messageHistories[sessionId] === undefined) {
        messageHistories[sessionId] = new InMemoryChatMessageHistory();
      }
      return messageHistories[sessionId];
    },

    inputMessagesKey: "input",
    historyMessagesKey: "chat_history",
  });

  const config = {
    configurable: {
      sessionId: sessionId,
    },
  };

  let response = null;

  try {
    response = await withMessageHistory.invoke(
      {
        input: message,
      },
      config
    );
  } catch (e) {
    console.error(e);
  }

  return {
    returnText: response?.content,
  };
};

export const generateResponseForPrompt = async (
  promptText,
  { contextList = [], max_gen_len = 8000 } = {}
) => {
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  const controller = new AbortController();

  const token = localStorage.getItem(`jwtToken`);
  if (!token) throw Error("No token found");

  const jwtPayload = jwtDecode(token);
  let tenantId = jwtPayload?.firebase?.tenant;

  contextList.push({ role: "user", text: promptText });

  let tempList = mergeObjectsByRole(
    contextList?.map((item) => ({
      role: item?.role,
      parts: [{ text: item?.text }],
    }))
  );

  console.log("finalContextList", tempList);

  const bodyOfRequest = {
    contents: tempList,
    safety_settings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
    ],
    generation_config: {
      temperature: 0,
      topP: 1,
      candidateCount: 1,
      maxOutputTokens: max_gen_len,
    },
  };
  let returnText = "";

  const response = await HTTPAidaBaseService.post(
    `/${tenantId}/submit-prompt`,
    { data: bodyOfRequest },
    { cancelToken: source.token, signal: controller.signal }
  );

  returnText = response?.data?.candidates?.[0]?.content?.parts?.[0]?.text;

  return { returnText, source, controller };
};

export const generateResponseFromGemini = async (
  promptText,
  { contextList = [], max_gen_len = 500 } = {}
) => {
  let tempList = contextList?.map((item) => ({
    role: item?.role,
    parts: { text: item?.text },
  }));

  tempList.push({ role: "user", parts: { text: promptText } });

  const bodyOfRequest = {
    contents: tempList,
    safety_settings: [
      {
        category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HATE_SPEECH",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_HARASSMENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
      {
        category: "HARM_CATEGORY_DANGEROUS_CONTENT",
        threshold: "BLOCK_ONLY_HIGH",
      },
    ],
    generation_config: {
      temperature: 0.2,
      topP: 0.2,
      candidateCount: 1,
      maxOutputTokens: max_gen_len,
    },
  };
  let returnText = "";

  const processDocument = httpsCallable(functionsInstance, "modelPredict");

  const documentData = {
    ...bodyOfRequest,
  };

  const response = await processDocument(documentData);

  response?.data?.forEach((item) => {
    const temp1 = item?.candidates?.[0]?.content;
    if (temp1?.role === "model") {
      returnText += temp1?.parts?.[0]?.text;
    }
  });

  return { returnText };
};

export const fixAndParseJsonFromGPT = (
  jsonString,
  { customPattern = null } = {}
) => {
  const objectRegex = customPattern
    ? customPattern
    : /\{(?:[^{}]|(?:\{(?:[^{}]|{\})*\}))*\}/g;
  let match;
  const objectsArrays = [];

  while ((match = objectRegex.exec(jsonString)) !== null) {
    try {
      let obj = match[0];
      // console.log("obj before", obj);
      // obj = obj?.replace(/(\r\n|\n|\r)/gm, "");

      // obj = obj.replace(/\/\/.*|\/\*[^]*?\*\//g, "");

      // obj = obj.replace(/(['"])?([a-zA-Z0-9_]+)(['"])?:/g, '"$2": ');

      // console.log("obj after", obj);

      if (typeof obj === "string" && obj.length > 0) {
        const repaired = JSON.parse(obj);
        objectsArrays.push(repaired);
      }

      // if (typeof obj === "object") {
      //   objectsArrays.push(obj);
      // }
    } catch (e) {
      console.error("Invalid JSON object:", match[0]);
    }
  }
  return objectsArrays;
};
export const getFhirSkeleton = (fhirResource, { } = {}) => {
  let resourceToReturn = {};
  if (fhirResource === "FHIR_CLINICAL_IMPRESSION") {
    resourceToReturn = {
      code: {
        coding: [
          {
            code: "51848-0",
            display: "Encounter specific assessment note",
            system: "https://loinc.org",
          },
        ],
        text: "Encounter specific assessment note",
      },
      description: "",
      effectivePeriod: {
        start: "",
      },
      encounter: {
        reference: "",
      },
      resourceType: "ClinicalImpression",
      status: "in-progress",
      subject: {
        reference: "",
      },
    };
  } else if (fhirResource === "FHIR_ENCOUNTER") {
    resourceToReturn = {
      class: {
        code: "308720009",
        display: "Untitled",
      },
      period: {
        start: "",
      },
      reasonCode: [
        {
          coding: [
            {
              display: "",
            },
          ],
          text: "",
        },
      ],
      resourceType: "Encounter",
      status: "in-progress",
      subject: {
        reference: "",
      },
      type: [
        {
          coding: [
            {
              code: "308720009",
              display: "Letter",
            },
          ],
        },
      ],
    };
  } else if (fhirResource === "FHIR_COMPOSITION") {
    resourceToReturn = {
      date: new Date().toISOString(),
      title: "",
      status: "preliminary",
      type: {
        coding: [
          {
            code: "00000-0",
            display: "gen-transcript",
            system: "https://loinc.org",
          },
        ],
      },
      subject: {
        reference: "",
      },
      encounter: {
        reference: "",
      },
      author: [
        {
          reference: "Practitioner/example",
        },
      ],
      section: [
        {
          title: "",
          text: {
            status: "generated",
            div: "",
          },
        },
      ],
      resourceType: "Composition",
    };
  }

  return resourceToReturn;
};

export const formatClinicalNoteAndRepair = (note) => {
  try {
  } catch (e) {
    console.error(e);
  }
  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let formattedWithMarked = marked?.parse(note);
  formattedWithMarked = formattedWithMarked?.replace(
    "<table>",
    `<table class='table'>`
  );
  return formattedWithMarked;
};

export const formatClinicalNote = (note) => {
  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let formattedWithMarked = marked?.parse(note);
  formattedWithMarked = formattedWithMarked?.replace(
    "<table>",
    `<table class='table'>`
  );
  return formattedWithMarked;
};

export const formatSoapNote = (note) => {
  if (_.isEmpty(note) || typeof note !== "string") {
    return "";
  }

  let lines = note.split("\n");

  const tableData = lines?.filter((line) => line.includes("|"));

  lines = lines.filter((line) => !line.includes("|"));

  const formattedLines = lines?.map((line) => {
    const boldRegex = /\*\*(.*?)\*\*/g;
    const boldMatches = line.matchAll(boldRegex);
    let formattedLine = line;
    for (const match of boldMatches) {
      formattedLine = formattedLine.replace(match[0], `<b>${match[1]}</b>`);
    }
    if (formattedLine.startsWith("*") && formattedLine?.length > 2) {
      formattedLine = `- ${formattedLine.substring(1).trim()}`;
    }
    return formattedLine.replace(/\*$/g, "");
  });

  if (tableData?.length > 0) {
    formattedLines?.push(table(tableData.join("\n")));
  }
  return formattedLines.join("<br/>");
};

const table = (dataString) => {
  // Split the string into rows
  let rows = dataString.split("\n").map((row) => row.trim());

  let elements = rows?.join("").split("|");

  let tempRows = [];
  let currentRow = [];

  elements.forEach((element) => {
    if (element === "") {
      if (currentRow.length > 0) {
        tempRows.push(currentRow.join("|"));
        currentRow = [];
      }
    } else {
      currentRow.push(element.trim());
    }
  });

  // Push the last row if it's not empty
  if (currentRow.length > 0) {
    tempRows.push(currentRow.join("|"));
  }

  rows = tempRows;

  const tableHtml = `
    <table class='table'>
      <thead>
        <tr>
          ${rows[0]
      .split("|")
      .map((cell) => `<th>${cell.trim()}</th>`)
      .join("")}
        </tr>
      </thead>
      <tbody>
        ${rows
      .slice(2)
      .map(
        (row) => `
          <tr>
            ${row
            .split("|")
            .map((cell) => `<td>${cell.trim()}</td>`)
            .join("")}
          </tr>
        `
      )
      .join("")}
      </tbody>
    </table>
  `;

  return tableHtml;
};

export const genderOptionList = [
  {
    label: "GENDER.MALE",
    value: "male",
  },
  {
    label: "GENDER.FEMALE",
    value: "female",
  },
  {
    label: "GENDER.OTHER",
    value: "other",
  },
  {
    label: "GENDER.UNKNOWN",
    value: "unknown",
  },
];

export const epicGenderOptionList = [
  {
    label: "GENDER.MALE",
    value: "male",
  },
  {
    label: "GENDER.FEMALE",
    value: "female",
  },
  {
    label: "GENDER.UNKNOWN",
    value: "unknown",
  },
];

export const FormError = ({ error, sx }) => {
  return (
    error && (
      <Typography
        variant="caption"
        component="p"
        sx={{ color: "#f44336", mt: 1, ...sx }}
      >
        {error}
      </Typography>
    )
  );
};

const formatTimeAgo = (count, singular, plural) =>
  `${count} ${count == 1 ? singular : plural} ago`;

export const discoverCreatedTime = (CreatedDate) => {
  const createdDate = dayjs(CreatedDate);
  const currentDate = dayjs();

  const diffInSeconds = currentDate.diff(createdDate, "second");
  const diffInMinutes = currentDate.diff(createdDate, "minute");
  const diffInHours = currentDate.diff(createdDate, "hour");
  const diffInDays = currentDate.diff(createdDate, "day");

  let timeAgo;
  if (diffInDays < 1) {
    if (diffInHours < 1) {
      if (diffInMinutes < 1) {
        timeAgo = formatTimeAgo(diffInSeconds, "second", "seconds");
        // timeAgo = `Created within an hour`;
      } else {
        timeAgo = formatTimeAgo(diffInMinutes, "minute", "minutes");
        // timeAgo = `Created within an hour`;
      }
    } else {
      timeAgo = formatTimeAgo(diffInHours, "hour", "hours");
    }
  } else {
    timeAgo = formatTimeAgo(diffInDays, "day", "days");
  }

  return timeAgo;
};

export const isJsonString = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export const cacheTenantIds = (data) => {
  if (!localStorage.getItem("tenants")) {
    localStorage.setItem("tenants", JSON.stringify([]));
  }
  let tenantsList = localStorage.getItem("tenants");
  let newTenantObj = {
    tenant: data?.tenantId,
    email: data?.email,
  };
  if (isJsonString(tenantsList)) {
    tenantsList = JSON.parse(tenantsList);
    if (tenantsList.filter((f) => f?.email == data?.email).length < 1) {
      tenantsList.push(newTenantObj);
      localStorage.setItem("tenants", JSON.stringify(tenantsList));
    }
  } else {
    localStorage.setItem("tenants", JSON.stringify([newTenantObj]));
  }
};

export const getCachedTenant = (email) => {
  if (!localStorage.getItem("tenants")) {
    localStorage.setItem("tenants", JSON.stringify([]));
  }
  let tenantsList = localStorage.getItem("tenants");
  if (isJsonString(tenantsList)) {
    tenantsList = JSON.parse(tenantsList);
    let tenantData = tenantsList.filter((f) => f?.email == email);
    if (tenantData.length) {
      return tenantData[0]?.tenant;
    } else return false;
  } else return false;
};

export const parseClinicalNote = (clinicalNote) => {
  try {
    // Remove leading and trailing ``` markers
    let cleanedNote = clinicalNote
      .replace(/^(?:```json|```JSON|```)/, "") // Remove leading ```json or ```JSON or ```
      .replace(/```$/, ""); // Remove trailing ```

    // Ensure keys are enclosed in double quotes
    cleanedNote = cleanedNote.replace(
      /([{,]\s*)([a-zA-Z0-9_]+)(\s*:)/g,
      '$1"$2"$3'
    );

    // Parse the cleaned JSON string
    return JSON.parse(cleanedNote);
  } catch (error) {
    console.error("Failed to parse clinical note:", error);
    return null; // or handle error as needed
  }
};

export const findOldestObject = (nestedArray) => {
  if (!Array.isArray(nestedArray)) {
    throw new TypeError("The input should be an array of arrays");
  }

  // Flatten the nested array
  const flattenedArray = nestedArray.flat();

  // Use reduce to find the oldest object
  const oldestObject = flattenedArray.reduce((oldest, current) => {
    if (current && current.resource && current.resource.date) {
      const currentDate = new Date(current.resource.date);
      if (!oldest || currentDate < new Date(oldest.resource.date)) {
        return current;
      }
    }
    return oldest;
  }, null);

  return oldestObject;
};

export function getMostRecentObject(objects) {
  if (!objects?.length) return null;

  return objects?.reduce((mostRecent, current) => {
    const mostRecentDate = new Date(mostRecent?.resource?.date);
    const currentDate = new Date(current?.resource?.date);
    return currentDate > mostRecentDate ? current : mostRecent;
  });
}

export const cleanName = (name) => {
  if (!name) {
    return false;
  }
  // Replace any comma followed by optional whitespace with a single space
  return name.replace(/,\s*/g, " ");
};

export const nullValues = [
  // English
  null, undefined, "null", "undefined", "", " ",
  "no data", "not available", "unknown", "empty", "nothing",

  // Spanish
  "null", "undefined", "nulo", "inexistente", "sin valor",
  "vacío", "nada", "sin datos", "no disponible", "desconocido",

  // French
  "nul", "indéfini", "sans valeur", "vide", "rien",
  "aucune donnée", "non disponible", "inconnu",

  // Polish
  "nieważne", "pusty", "brak danych", "nic", "nieznane",
  "niedostępne", "null", "nieokreślone",

  // Uppercase versions for all languages
  "NULL", "UNDEFINED", "NULO", "INEXISTENTE", "SIN VALOR",
  "VACÍO", "NADA", "SIN DATOS", "NO DISPONIBLE", "DESCONOCIDO",
  "NUL", "INDÉFINI", "SANS VALEUR", "VIDE", "RIEN",
  "AUCUNE DONNÉE", "NON DISPONIBLE", "INCONNU",
  "NIEWAŻNE", "PUSTY", "BRAK DANYCH", "NIC", "NIEZNANE", "NIEDOSTĘPNE",

  // Capitalized versions for all languages
  "Null", "Undefined", "Nulo", "Inexistente", "Sin valor",
  "Vacío", "Nada", "Sin datos", "No disponible", "Desconocido",
  "Nul", "Indéfini", "Sans valeur", "Vide", "Rien",
  "Aucune donnée", "Non disponible", "Inconnu",
  "Nieważne", "Pusty", "Brak danych", "Nic", "Nieznane", "Niedostępne"
];


export function replaceNulls(obj) {
  try {
    if (
      nullValues?.includes(obj) ||
      isEmpty(obj)
    ) {
      return "";
    } else if (Array.isArray(obj)) {
      if (
        obj.length === 0 ||
        obj.every((item) =>
          nullValues?.includes(item)
        )
      ) {
        return [];
      }
      if (obj?.length > 0) {
        obj = obj?.filter(
          (f) => !nullValues?.includes(f)
        );
      }
      return obj.map(replaceNulls);
    } else if (typeof obj === "object") {
      const newObj = {};
      for (let key in obj) {
        if (obj.hasOwnProperty(key)) {
          newObj[key] = replaceNulls(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  } catch (e) {
    console.error(e);
  }
}

export const formatScaleNoteTenantSpecific = (obj) => {

  try {

    let score = 0;

    score += Number(obj?.['Fecal Incontinence Score']?.['Incontinence to Solid Stool'] ?? 0);
    score += Number(obj?.['Fecal Incontinence Score']?.['Incontinence to Liquid Stool'] ?? 0);
    score += Number(obj?.['Fecal Incontinence Score']?.['Incontinence to Gas'] ?? 0);
    score += Number(obj?.['Fecal Incontinence Score']?.['Wears Pad'] ?? 0);
    score += Number(obj?.['Fecal Incontinence Score']?.['Lifestyle Alteration'] ?? 0);


    obj['Fecal Incontinence Score']['Total Score'] = score + '';
    obj['Fecal Incontinence Score Summary']['Score'] = score + '';

    return obj;

  } catch (e) {
    return obj;
  }
}

export const getCurrentRegenValue = (arr, selectedOption1) => {
  let selectedOption = JSON.parse(JSON.stringify(selectedOption1));
  let g;
  let regenerateRawObject;

  const isTranslateSOAPNote =
    selectedOption?.label?.includes("Translate SOAP Note to");

  if (isTranslateSOAPNote) {
    selectedOption.prompt_name = arr["SOAP Note for First Visit"]
      ? "SOAP Note for First Visit"
      : "SOAP Note for Follow-up Visit";
    regenerateRawObject = arr?.[selectedOption?.prompt_name]?.component?.find(
      (element) => {
        const code = element?.code?.coding?.[0]?.code;
        return code === "translate-soap";
      }
    );
    try {
      g = Number(regenerateRawObject?.valueInteger);
    } catch (e) {
      console.error(e);
    }
  } else {
    regenerateRawObject = arr?.[selectedOption?.prompt_name]?.component?.find(
      (element) => {
        const code = element?.code?.coding?.[0]?.code;
        return code !== "translate-soap";
      }
    );
    try {
      g = Number(regenerateRawObject?.valueInteger);
    } catch (e) {
      console.error(e);
    }
  }

  return g || 0;
};
export const getOldestTitle = (arr) => {
  if (!arr?.length) return null;

  // Find the oldest object by comparing resource.date
  const oldestObj = arr?.reduce((oldest, current) => {
    return new Date(current?.resource?.date) < new Date(oldest?.resource?.date) ? current : oldest;
  });

  return oldestObj?.resource?.title || 'No Title Found';
};

export const exceptionalRoutes = ['verify', 'business-associate-agreement', 'reset-password', 'login', 'signup']


export const handleBulkDeleteEncounter = async (encounters) => {
  if (!encounters || encounters?.length === 0) {
    console.log("No ready to delete encounters found for this practitioner");
    return;
  }
  try {
    const bulkUpdateRequest = {
      resourceType: "Bundle",
      type: "transaction",
      entry: encounters?.map((comp) => ({
        resource: {
          ...comp?.resource,
          extension: [
            {
              url: "http://example.org/fhir/StructureDefinition/modifiedAt",
              valueDateTime: new Date().toISOString(),
            },
          ],
          status: "cancelled",
        },
        request: {
          method: "PUT",
          url: `Encounter/${comp?.resource?.id}`,
        },
      })),
    };

    const res = await FHIRSave("executeBundle", bulkUpdateRequest);
  } catch (e) {
    console.error("error while bulk deleting enc", e);
  }

};

export function filterOldObjectsWithModifiedDays(data, days) {
  return data
    ?.filter(obj => {
      const modifiedAt = obj?.resource?.extension?.find(ext => ext?.url === "http://example.org/fhir/StructureDefinition/modifiedAt")?.valueDateTime;

      if (modifiedAt) {
        const today = dayjs();
        const modifiedDate = dayjs(modifiedAt);
        const targetDate = today.subtract(days, 'day'); // Get the target date by subtracting 'days'

        // Check if modifiedAt is on or before the target date (accurate to date and time)
        return modifiedDate.isBefore(targetDate) || modifiedDate.isSame(targetDate);
      }

      return false; // If no modifiedAt date, exclude the object
    })
    .map(obj => {
      const modifiedAt = obj?.resource?.extension?.find(ext => ext.url === "http://example.org/fhir/StructureDefinition/modifiedAt")?.valueDateTime;
      const modifiedDate = dayjs(modifiedAt);
      const daysAgo = dayjs().diff(modifiedDate, 'day'); // Calculate days ago

      return {
        ...obj,
        modifiedDaysAgo: daysAgo // Add how many days ago the object was modified
      };
    });
}

dayjs.extend(utc);

// i18n is a parameter rather than a module import because calculateDeletionTime
// receives its own instance from the caller.
// Shared by containers/App.jsx and mobile/MobileApp.jsx, whose bootstrap paths both
// normalise the saved display language the same way. Mutates savedSettings in place,
// exactly as the inline blocks did, and returns whether the trial/welcome screen is
// due so the caller keeps ownership of its own shouldShowTrialScreen flag.
export const normalizeSettingsLanguage = (savedSettings) => {
  const supportedLanguages = ["en", "es", "fr-FR", "pl-PL"];
  if (
    !savedSettings?.language ||
    !supportedLanguages.includes(savedSettings?.language)
  ) {
    const systemLanguage =
      navigator?.language || navigator?.userLanguage || "en";
    const languageCode = systemLanguage.split("-")[0];
    savedSettings.language = languageCode || "en";
  }
  return savedSettings?.isWelcomeScreenShown === false;
};

const unitLabel = (count, singular, plural, i18n) =>
  i18n.t(`common.${count == 1 ? singular : plural}`);

export function calculateDeletionTime(deleteAfterDays = 90, data, i18n) {
  // const modifiedAt = data?.encounter?.resource?.extension?.[0]?.valueDateTime;

  const modifiedAt = dayjs(data?.encounter?.resource?.extension?.[0]?.valueDateTime).utc(); //converting date into utc using dayjs

  // Calculating the deletion date
  const deletionDate = dayjs(modifiedAt).add(deleteAfterDays, "day");

  // const now = dayjs()
  const now = dayjs.utc();

  // Calculating the total difference in minutes and seconds
  const totalMinutesDifference = deletionDate.diff(now, "minute");
  const totalSecondsDifference = deletionDate.diff(now, "second");

  const remainingDays = Math.floor(totalMinutesDifference / 1440); // 1440 minutes in a day
  const remainingHours = Math.floor((totalMinutesDifference % 1440) / 60);
  const remainingMinutes = totalMinutesDifference % 60;

  // Handling overdue cases (only if occurs)
  if (totalMinutesDifference < 0) {
    const overdueDays = Math.abs(remainingDays);
    const overdueHours = Math.abs(remainingHours);
    const overdueMinutes = Math.abs(remainingMinutes);
    const overdueSeconds = Math.abs(totalSecondsDifference);

    if (overdueSeconds < 60) {
      return i18n.t("common.OVERDUE_BY_LESS_THAN_A_MINUTE");
    } else if (overdueMinutes > 0 && overdueMinutes < 60 && overdueHours < 0 && overdueDays < 0) {
      return `${i18n.t("common.OVERDUE_BY")} ${overdueMinutes} ${unitLabel(
        overdueMinutes,
        "MINUTE",
        "MINUTES",
        i18n
      )}`;
    } else if (overdueHours > 0 && overdueHours < 24 && overdueDays < 0) {
      return `${i18n.t("common.OVERDUE_BY")} ${overdueHours} ${unitLabel(
        overdueHours,
        "HOUR",
        "HOURS",
        i18n
      )}`;
    } else if (overdueDays > 0) {
      return `${i18n.t("common.OVERDUE_BY")} ${overdueDays} ${unitLabel(
        overdueDays,
        "DAY",
        "DAYS",
        i18n
      )}`;
    }
  }

  // For non-overdue records
  if (remainingDays > 0) {
    return `${remainingDays} ${unitLabel(remainingDays, "DAY", "DAYS", i18n)}`;
  } else if (remainingHours > 0) {
    return `${remainingHours} ${unitLabel(remainingHours, "HOUR", "HOURS", i18n)}`;
  } else if (remainingMinutes > 0) {
    return `${remainingMinutes} ${unitLabel(
      remainingMinutes,
      "MINUTE",
      "MINUTES",
      i18n
    )}`;
  } else {
    return i18n.t("common.LESS_THAN_A_MINUTE");
  }
}

const customStyleInput = {
  ".MuiFilledInput-root": {
    // background: "rgba(37, 26, 61, 1)",
    background: "#201F47 !important",
    border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
    // fontFamily:"Nunito",
    overflow: "hidden",
  },
  ".Mui-disabled": {
    color: "rgba(255, 255, 255, 0.38) !important",
    "-webkit-text-fill-color": "rgba(255, 255, 255, 0.38) !important",
  },
  ".MuiFilledInput-input": {
    color: "white !important",
    // paddingTop: "clamp(1.1vh, 1.1vw, 1.1vw)",
    // paddingRight: "clamp(0.5vh, 0.5vw, 0.5vw)",
    // paddingBottom: "clamp(0.1vh, 0.1vw, 0.1vw)",
    paddingBottom: "clamp(0.1rem, 0.1vw, 0.1vw)",
    background: "rgba(37, 26, 61, 1)",
    // border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
    fontSize: "1vw",
    // fontFamily:"Nunito"
  },
  ".MuiInputLabel-root": {
    color: "white !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    // fontFamily:"Numito"
  },
  ".MuiInputBase-input::placeholder": {
    color: "white !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
  },
  ".MuiFilledInput-root:before": {
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    borderBottom: "none !important",
  },
  ".MuiFilledInput-root:after": {
    borderBottom: "none !important",
  },
  // Add custom styles for Select and MenuItem components
  ".MuiSelect-root": {
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    // fontSize:"5px"
    // background: "rgba(37, 26, 61, 1)",
    // border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    // borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
  },
  ".MuiSelect-icon": {
    color: "white !important",
  },
  ".MuiMenu-paper": {
    background: "rgba(37, 26, 61, 1) !important",
    color: "white !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
  },
  ".MuiMenuItem-root": {
    background: "rgba(37, 26, 61, 1) !important",
    color: "white !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    "&:hover": {
      background: "rgba(50, 40, 75, 1) !important",
    },
    "&.Mui-selected": {
      background: "rgba(50, 40, 75, 1) !important",
      fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
      "&:hover": {
        background: "rgba(60, 50, 85, 1) !important",
      },
    },
  },
  "MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
    borderBottom: "none !important",
  },
};

const customStyleInputDark = {
  ".MuiFilledInput-root": {
    // background: "rgba(37, 26, 61, 1)",
    // background: "#201F47 !important",
    border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
    // fontFamily:"Nunito",
    overflow: "hidden",
  },
  ".Mui-disabled": {
    color: "#000 !important",
    "-webkit-text-fill-color": "#000 !important",
  },
  ".MuiFilledInput-input": {
    color: "black !important",
    // paddingTop: "clamp(1.1vh, 1.1vw, 1.1vw)",
    // paddingRight: "clamp(0.5vh, 0.5vw, 0.5vw)",
    // paddingBottom: "clamp(0.1vh, 0.1vw, 0.1vw)",
    paddingBottom: "clamp(0.1rem, 0.1vw, 0.1vw)",
    // background: "rgba(37, 26, 61, 1)",
    // border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
    fontSize: "1vw",
    // fontFamily:"Nunito"
  },
  ".MuiInputLabel-root": {
    color: "black !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    // fontFamily:"Numito"
  },
  ".MuiInputBase-input::placeholder": {
    color: "black !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
  },
  ".MuiFilledInput-root:before": {
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    borderBottom: "none !important",
  },
  ".MuiFilledInput-root:after": {
    borderBottom: "none !important",
  },
  // Add custom styles for Select and MenuItem components
  ".MuiSelect-root": {
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    // fontSize:"5px"
    // background: "rgba(37, 26, 61, 1)",
    // border: "clamp(0.1vh, 0.1vw, 0.1vw) solid rgba(255, 255, 255, 0.6)",
    // borderRadius: "clamp(0.5vh, 0.5vw, 0.5vw) !important",
  },
  ".MuiSelect-icon": {
    color: "black !important",
  },
  ".MuiMenu-paper": {
    // background: "rgba(37, 26, 61, 1) !important",
    color: "black !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
  },
  ".MuiMenuItem-root": {
    // background: "rgba(37, 26, 61, 1) !important",
    color: "black !important",
    fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
    "&:hover": {
      // background: "rgba(50, 40, 75, 1) !important",
    },
    "&.Mui-selected": {
      // background: "rgba(50, 40, 75, 1) !important",
      fontSize: "clamp(0.8vh, 0.8vw, 0.8vw)",
      "&:hover": {
        // background: "rgba(60, 50, 85, 1) !important",
      },
    },
  },
  "MuiFilledInput-root:hover:not(.Mui-disabled, .Mui-error):before": {
    borderBottom: "none !important",
  },
};

export const getCSS = (theme) => {
  return theme === 'light' ? customStyleInputDark : customStyleInput
}

// Helper function to remove specified suffixes from the title
export const removeSuffixes = (title, suffixes) => {
  for (let suffix of suffixes) {
    if (title?.endsWith(suffix)) {
      return title?.slice(0, -suffix.length);
    }
  }
  return title;
};

// Array of suffixes to check and remove
export const suffixes = [" for First Visit", " for Follow-up", " for Consultation", " for Previous Visit", " for Follow-up Visit"];

export function getLatestComposition(arr) {
  return arr.reduce((latest, current) => {
    const latestDate = new Date(latest?.resource?.date);
    const currentDate = new Date(current?.resource?.date);
    return currentDate > latestDate ? current : latest;
  })
}

export function isNewCDSSTitle(cdssObject) {
  if (!cdssObject || typeof cdssObject !== "object") return false;

  const newKeys = [
    "Suggested Diagnostic Testing (Prioritized by Differential Diagnoses)",
    "Suggested Differential Diagnoses (Base) & Interventions",
    "Suggested Medications (Tailored to Diagnoses)",
    "Suggested Questions to Ask (Tailored to Differential Diagnoses)",
  ];

  return newKeys.some((key) => key in cdssObject);
}

export function reformatData(data, i18n, languageCode) {
  // Creating a deep copy of the input data using JSON methods
  const input = JSON.parse(JSON.stringify(data));
  const output = {};

  const getTitle = (text) => {
    switch (text) {
      case "suggestedMedications":
        return i18n.t("Patient_Screen.SUGGESTED_MEDICATIONS", {
          lng: languageCode,
        });

      case "suggestedFollowups":
        return i18n.t("Patient_Screen.SUGGESTED_FOLLOWUPS", {
          lng: languageCode,
        });

      case "suggestedDiagnosis&Interventions":
        return i18n.t("Patient_Screen.SUGGESTED_DIAGNOSIS_AND_INTERVENTIONS", {
          lng: languageCode,
        });

      case "suggestedQuestions":
        return i18n.t("Patient_Screen.SUGGESTED_QUESTIONS", {
          lng: languageCode,
        });

      default:
        return "";
    }
  };

  for (const key in input) {
    if (Array.isArray(input[key])) {
      output[getTitle(key)] = input[key].map(item => {
        // Getting the appropriate key and value pair (either "question" or "title" with "description").
        const mainKey = item.question || item.title;
        const description = item.description;
        return `${mainKey}: ${description}`;
      });
    }
  }
  return output;
}

export const killIntervals = () => {
  let id = window.setTimeout(function () { }, 0);

  while (id--) {
    window.clearTimeout(id); // will do nothing if no timeout with id is present
  }
  // Get a reference to the last interval + 1
  const interval_id = window.setInterval(function () { }, Number.MAX_SAFE_INTEGER);

  // Clear any timeout/interval up to that id
  for (let i = 1; i < interval_id; i++) {
    window.clearInterval(i);
  }
}

const CDSS_COLOR_MAP = {
  CDSS_FROM_PREV_SOAP_NOTE: "#496a9e",
  CDSS_FROM_DEMOGRAPHICS: "#b5651d",
  CDSS_FROM_CONVERSATION: "#2196F3",
};

export const cdssCustomTitleValidator = (text) => {
  if (CDSS_COLOR_MAP[text]) {
    return { color: CDSS_COLOR_MAP[text], isValid: true };
  }
  return { color: null, isValid: false };
};

export function transformActionString(template) {
  // Split the label by space to get the first element
  const labelParts = template?.label?.split(' ') || [];

  // Ensure the first element of label is replaced with the appropriate localized value
  switch (template?.promptType) {
    case "Generate":
      return `${i18n.t("promptLabels.GENERATING")} ${labelParts?.slice(1)?.join(' ')}`;
    case "Regenerate":
      return `${i18n.t("promptLabels.REGENERATING")} ${labelParts?.slice(1)?.join(' ')}`;
    case "translate":
      return `${i18n.t("promptLabels.TRANSLATING")} ${labelParts?.slice(1)?.join(' ')}`;
    default:
      return template?.label; // Fallback, though not necessary here
  }
}

export function mapToCDSSSyntax(inputData) {
  // Helper function to create the `qalist` format
  const createQalist = (list, keyMappings) => {
    return list.map(item => {
      let mappedItem = {};
      keyMappings.forEach(key => {
        if (item[key]) {
          mappedItem[key] = item[key];
        }
      });
      return mappedItem;
    });
  };

  // Define the mapping structure
  const cdssData = [
    {
      title: "speaker_identification",
      guest1: "doctor",
      guest2: "patient"
    },
    {
      title: "suggestedDiagnosis&Interventions",
      qalist: createQalist((inputData['suggestedDiagnosis&Interventions'] || inputData['Suggested Diagnosis and Intervention']) || [], ["title", "description", "question"])
    },
    {
      title: "suggestedQuestions",
      qalist: createQalist((inputData.suggestedQuestions || inputData['Suggested Questions']) || [], ["question", "description"])
    },
    {
      title: "suggestedMedications",
      qalist: createQalist((inputData.suggestedMedications || inputData['Suggested Medications']) || [], ["title", "description"])
    },
    {
      title: "suggestedFollowups",
      qalist: createQalist((inputData.suggestedFollowups || inputData['Suggested Followups']) || [], ["title", "description"])
    }
  ];

  // Convert to the desired output syntax
  return `CDSS: ${JSON.stringify(cdssData)}`;
}

export const getDeviceDetails = (deviceName) => {
  const deviceMappings = {
    "Android Device (Mobile)": { value: 1, osName: "Android", redirectLink: "https://play.google.com/store/apps/details?id=com.thinkhat.heynoki" },
    "iPad (Tablet)": { value: 2, osName: "iOS", redirectLink: "https://apps.apple.com/in/app/hey-noki/id6526487632" },
    "iOS Device (Mobile)": { value: 3, osName: "iOS", redirectLink: "https://apps.apple.com/in/app/hey-noki/id6526487632" },
    "Windows Phone (Mobile)": { value: 4, osName: "Windows" },
    "Windows PC": { value: 5, osName: "Windows" },
    "Mac (Mobile)": { value: 6, osName: "macOS" },
    "Mac": { value: 7, osName: "macOS" },
    "Linux Device (Mobile)": { value: 8, osName: "Linux" },
    "Linux PC": { value: 9, osName: "Linux" },
    "Unknown Device": { value: 0, osName: "Unknown" },
  };

  return {
    deviceName: deviceName,
    value: deviceMappings[deviceName]?.value || 0,
    osName: deviceMappings[deviceName]?.osName || "Unknown",
    redirectLink: deviceMappings[deviceName]?.redirectLink || null
  };
};

export const restrictValidation = () => {
  return ["shinigami-sama-34eob", "cc-bk-xyknr", "victoria-segro-qjpae", "jennifer-farren-kc4za", "meagen-salinas-0oznc", "cc-cherukuri-lfsv9", "md-akhtar-nawab-sidd-vifdr"].includes(getTenantFromToken());
}

export const mappedLanguages = {
  "en": {
    languageCode: "en",
    dateFormat: "MM/DD/YYYY",
    timeFormat: "hh:mm A", // 12-hour format with AM/PM
    timeWithDateFormat: "MM/DD/YYYY hh:mm A" // Date + Time (12-hour format)
  },
  "es": {
    languageCode: "es",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD/MM/YYYY HH:mm" // Date + Time (24-hour format)
  },
  "fr-FR": {
    languageCode: "fr",
    dateFormat: "DD/MM/YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD/MM/YYYY HH:mm" // Date + Time (24-hour format)
  },
  "pl-PL": {
    languageCode: "pl",
    dateFormat: "DD.MM.YYYY",
    timeFormat: "HH:mm", // 24-hour format
    timeWithDateFormat: "DD.MM.YYYY HH:mm" // Date + Time (24-hour format)
  }
};

export const removeQueryParams = (params) => {
  const url = new URL(window.location.href);

  // Loop through the provided array and remove each query parameter
  params.forEach(param => url.searchParams.delete(param));

  // Update the URL without reloading the page
  window.history.pushState({}, "", url.pathname + url.search);
};

export const clearPatientAndEncounter = (dispatch) => {
  dispatch(patientObject({}));
  dispatch(encounterObject({}));
};


export const isDraftCheck = (visit) => {
  if (visit?.title === "Draft Transcript") {
    return true;
  }
  const status = visit?.meta?.tag?.find(
    (tag) => tag?.code === 'draft'
  );
  if (status) {
    return true;
  }

  return false
}

export const removeKeys = (obj, keys) => {
  keys.forEach((key) => {
    if (obj.hasOwnProperty(key)) {
      delete obj[key];
    }
  });
};